import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

import {
  LogIn,
  ShieldCheck,
  Loader2,
} from "lucide-react";

import { showError, showSuccess } from "../utils/toast";
import { JWT, ROUTES } from "../config/constants";
import BottomImage from "../assets/logocqhse.png";
import Button from "../components/Button";

function Login() {
  const [nip, setNip] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [forgotPassword, setForgotPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);

  const countdownRef = useRef(null);

  const navigate = useNavigate();

  const BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8085/api";

  useEffect(() => {
    const savedNip = localStorage.getItem("remenberEmail");

    if (savedNip) {
      setNip(savedNip);
      setRememberMe(true);
    }

    return () => {
      if (countdownRef.current)
        clearInterval(countdownRef.current);
    };
  }, []);

  const startCountdown = (seconds) => {
    if (countdownRef.current)
      clearInterval(countdownRef.current);

    setRateLimitCountdown(seconds);

    countdownRef.current = setInterval(() => {
      setRateLimitCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  };

  const isRateLimited = rateLimitCountdown > 0;

  const handleLogin = async (e) => {
    e.preventDefault();

    if (isRateLimited) return;

    setLoading(true);

    try {
      const res = await axios.post(`${BASE_URL}/login`, {
        email: nip,
        password,
      });

      localStorage.setItem(JWT.TOKEN_KEY, res.data.token);
      localStorage.setItem(JWT.EMAIL_KEY, res.data.email || nip);
      localStorage.setItem(JWT.NAMA_KEY, res.data.nama);
      localStorage.setItem(
        JWT.PROJECT_ID_KEY,
        res.data.project_id || "",
      );
      localStorage.setItem(
        JWT.PROJECT_NAME_KEY,
        res.data.project_name || "",
      );
      localStorage.setItem(
        JWT.ROLE_KEY,
        res.data.role || "user",
      );

      if (rememberMe) {
        localStorage.setItem("remenberEmail", nip);
      } else {
        localStorage.removeItem("remenberEmail");
      }

      navigate(ROUTES.PORTAL);
      window.location.reload();
    } catch (err) {
      console.error("Login Error Detail:", err);

      if (err.response) {
        const status = err.response.status;

        const msgDariBackend =
          err.response.data?.message ||
          err.response.data?.error;

        if (status === 429) {
          const retryAfter =
            err.response.data?.retry_after;

          const waitSeconds = retryAfter
            ? Math.ceil(retryAfter - Date.now() / 1000)
            : 60;

          startCountdown(Math.max(waitSeconds, 10));

          showError(
            "Terlalu banyak percobaan login. Silakan tunggu sebelum mencoba lagi.",
          );
        } else if (status === 400) {
          showError(
            msgDariBackend ||
              "Data yang dikirim tidak valid.",
          );
        } else if (status === 401) {
          showError(
            "Email dan password salah. Silakan coba lagi.",
          );
        } else if (status === 404) {
          showError("User tidak ditemukan.");
        } else if (status >= 500) {
          showError(
            "Terjadi gangguan pada Server/Database. Hubungi Admin.",
          );
        } else {
          showError(
            msgDariBackend ||
              `Terjadi kesalahan. Kode: ${status}`,
          );
        }
      } else if (err.request) {
        showError(
          "Tidak dapat terhubung ke Server. Silahkan periksa koneksi Anda atau coba lagi nanti.",
        );
      } else {
        showError(
          "Terjadi kesalahan pada aplikasi: " + err.message,
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      await axios.post(`${BASE_URL}/lupa`, { email });

      showSuccess(
        "Silakan cek email Anda untuk reset password",
      );
    } catch (err) {
      console.error("Forgot password error:", err);

      showError(
        "Gagal mengirim permintaan reset password",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCountdown = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;

    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-white flex items-center justify-center px-6 py-10">
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-[-120px] left-[-80px] w-[380px] h-[380px] bg-cyan-200/40 rounded-full blur-3xl animate-pulse" />

        <div
          className="absolute bottom-[-120px] right-[-80px] w-[420px] h-[420px] bg-blue-200/40 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "6s" }}
        />

        <div
          className="absolute top-[45%] left-[45%] w-[260px] h-[260px] bg-sky-200/30 rounded-full blur-3xl animate-pulse"
          style={{ animationDuration: "8s" }}
        />

        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] bg-[size:50px_50px]" />
      </div>

      {/* Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="relative overflow-hidden rounded-[32px] border border-gray-200 bg-white/70 backdrop-blur-2xl shadow-2xl p-8">
          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-white/40" />

          <div className="relative z-10">
            {/* Header */}
            <div className="flex flex-col items-center text-center mb-8">
              <div className="w-20 h-20 rounded-3xl bg-cyan-50 border border-cyan-100 flex items-center justify-center shadow-sm mb-5">
                <img
                  src={BottomImage}
                  alt="Logo"
                  className="w-12 h-12 object-contain"
                />
              </div>

              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gray-200 bg-white shadow-sm mb-4">
                <ShieldCheck
                  size={14}
                  className="text-cyan-600"
                />

                <span className="text-[11px] uppercase tracking-widest text-gray-600">
                  Secure Login
                </span>
              </div>

              <h1 className="text-3xl font-semibold text-gray-900">
                Welcome Back
              </h1>

              <p className="text-sm text-gray-500 mt-3 leading-relaxed">
                Engineering Strategic Digital Platform
              </p>
            </div>

            {/* Rate Limit */}
            {isRateLimited && (
              <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3">
                <p className="text-sm font-semibold text-red-700">
                  ⚠️ Terlalu banyak percobaan
                </p>

                <p className="text-sm text-red-600 mt-1">
                  Coba lagi dalam{" "}
                  <span className="font-bold">
                    {formatCountdown(
                      rateLimitCountdown,
                    )}
                  </span>
                </p>
              </div>
            )}

            {forgotPassword ? (
              <form
                onSubmit={handleForgotPassword}
                className="flex flex-col gap-4"
              >
                <input
                  type="email"
                  placeholder="Masukkan Email"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                  className="h-12 px-4 rounded-2xl border border-gray-200 bg-white/80 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition"
                  required
                  disabled={loading}
                />

                <Button
                  onClick={handleForgotPassword}
                  variant="primary"
                  className="h-12 rounded-2xl text-sm font-medium mt-2"
                >
                  {loading
                    ? "Mengirim..."
                    : "Kirim Link Reset"}
                </Button>

                <button
                  type="button"
                  className="text-cyan-600 text-sm hover:underline mt-2"
                  onClick={() =>
                    setForgotPassword(false)
                  }
                >
                  Back to login
                </button>
              </form>
            ) : (
              <form
                onSubmit={handleLogin}
                className="flex flex-col gap-4"
              >
                <input
                  type="text"
                  placeholder="Email"
                  value={nip}
                  onChange={(e) =>
                    setNip(e.target.value)
                  }
                  className="h-12 px-4 rounded-2xl border border-gray-200 bg-white/80 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition"
                  disabled={
                    loading || isRateLimited
                  }
                />

                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) =>
                    setPassword(e.target.value)
                  }
                  className="h-12 px-4 rounded-2xl border border-gray-200 bg-white/80 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 transition"
                  disabled={
                    loading || isRateLimited
                  }
                />

                {/* Remember */}
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={rememberMe}
                      onClick={() =>
                        setRememberMe(!rememberMe)
                      }
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                        rememberMe
                          ? "bg-cyan-600 border-cyan-600"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      {rememberMe && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={3}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </button>

                    <label
                      onClick={() =>
                        setRememberMe(!rememberMe)
                      }
                      className="text-sm text-gray-600 cursor-pointer select-none"
                    >
                      Remember Me
                    </label>
                  </div>
                </div>

                {/* Login Button */}
                <Button
                  onClick={handleLogin}
                  variant="primary"
                  className={`h-12 rounded-2xl text-sm font-medium mt-3 ${
                    isRateLimited
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  disabled={
                    loading || isRateLimited
                  }
                >
                  <div className="flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Loader2
                          size={16}
                          className="animate-spin"
                        />
                        Loading...
                      </>
                    ) : isRateLimited ? (
                      `Tunggu ${formatCountdown(
                        rateLimitCountdown,
                      )}`
                    ) : (
                      <>
                        <LogIn size={16} />
                        Login
                      </>
                    )}
                  </div>
                </Button>
              </form>
            )}

            {/* Footer */}
            <div className="mt-8 flex flex-col items-center gap-4">
              <p className="text-xs text-gray-400">
                Version 1.0.0
              </p>

          
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
